//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region Angular Imports

import { ITimeoutService, IComponentController } from 'angular'

//#endregion

//#region IMR Imports

import { IMRFullscreenContainerOptions, IIMRFullscreenContainer} from '@imr/controls.angularjs/components/fullscreen-container'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region Service Imports

import { UserSessionService } from '../../../services/user-session'
import { MeasurementsService } from '../measurements/measurements.service'

//#endregion

import { ConvertHelper, ToastHelper, LocalizationHelper, LogHelper, TypeHelper, TemplateHelper, ConfigHelper, Dictionary } from '../../../helpers'

import { IMRElementSizeWatchOptions } from '../element-size-watch'
import { IMROverlayContainerOptions, IIMROverlayContainer } from '../overlay-container'

import { BaseChartExtension } from './extensions'


// Public Types

export interface IIMRChart<TChartExtension> {
    extension: TChartExtension

    clear(): void
    refresh(): void
}

export type IMRChartOptions<TChartExtension> = {
    extension?: IIMRChartExtension

    fullscreen?: {
        enabled?: boolean
    }

    toolbar?: {
        enabled?: boolean
    }

    onInitialized?(imrChart: IIMRChart<TChartExtension>): void
    onStateStoring?(state: any): void
    onStateRestoring?(): any
}

// Public Types End


// Private Types

type ChartState = {
    isLegendVisible?: boolean
    isRotated?: boolean
    isTooltipShared?: boolean
}

// Private Types End


// Extension Types

export interface IIMRChartExtension {
    extensionName: string

    clear(): void
    getChartOptions(): DevExpress.viz.charts.dxChartOptions
    getPieChartOptions(): DevExpress.viz.charts.dxPieChartOptions
    initialize(chartController: IIMRChartExtensionController): JQueryPromise<void>
    refresh(): JQueryPromise<void>
}

export interface IIMRChartExtensionController {
    clearOverlayText(): void
    getExtensionState(): any
    getToolbarItems(): any[]
    getUserSession(): UserSessionService
    getMeasurementService(): MeasurementsService
    hideLoading(): void
    hideSeriesAxisPermanently(seriesConfig: DevExpress.viz.ChartSeries): void
    onError(error: any): void
    showLoading(): void
    showNoDataText(): void
    storeExtensionState(state: any): void
    toggleSeries(seriesConfig: DevExpress.viz.ChartSeries): void
}

// Extension Types End


export class IMRChartController<TChartExtension> implements IComponentController, IIMRChart<TChartExtension>, IIMRChartExtensionController {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Common.IMRChart]' }

    // Consts End


    // Members

    private isLoading: boolean
    private isChartLoading: boolean

    private options: IMRChartOptions<TChartExtension>

    private currentExtension: IIMRChartExtension

    private chart: DevExpress.viz.dxChart
    private chartOptions: DevExpress.viz.charts.dxChartOptions
    private pieChart: DevExpress.viz.dxPieChart
    private pieChartOptions: DevExpress.viz.charts.dxPieChartOptions

    private topToolbarOptions: DevExpress.ui.dxToolbarOptions

    private elementSizeWatchOptions: IMRElementSizeWatchOptions

    private chartOverlayContainer: IIMROverlayContainer
    private chartOverlayContainerOptions: IMROverlayContainerOptions

    private fullscreenContainer: IIMRFullscreenContainer
    private fullscreenContainerOptions: IMRFullscreenContainerOptions

    private isToolbarEnabled: boolean
    private isFullscreenEnabled: boolean
    private isFullscreen: boolean

    private axisTitles: Dictionary<string>
    private hiddenSeriesAxis: string[]

    // Members End


    // Ctor

    constructor(private readonly $timeout: ITimeoutService, private readonly userSession: UserSessionService, private readonly measurementsService: MeasurementsService) { }

    // Ctor End


    // IIMRChart Implementation

    public get extension(): TChartExtension { return this.currentExtension as any as TChartExtension }

    public clear(): void {
        if (this.currentExtension != null) {
            this.currentExtension.clear()
        }
    }

    public refresh(): void {
        if (this.currentExtension != null) {
            this.isChartLoading = true

            this.currentExtension
                .refresh()
                .always(() => this.isChartLoading = false)
        }
    }

    // IIMRChart Implementation End


    // IIMRChartExtensionController Implementation

    public getExtensionState(): any {
        const extensionState: any = this.onStateRestoring()

        if (extensionState != null) {
            return extensionState[this.currentExtension.extensionName]
        }

        return null
    }

    public getToolbarItems(): any[] {
        return this.topToolbarOptions.items
    }

    public getUserSession(): UserSessionService {
        return this.userSession
    }

    public getMeasurementService(): MeasurementsService {
        return this.measurementsService
    }

    public hideSeriesAxisPermanently(seriesConfig: DevExpress.viz.ChartSeries) {
        if (seriesConfig != null && !_.contains(this.hiddenSeriesAxis, seriesConfig.name)) {
            this.hiddenSeriesAxis.push(seriesConfig.name)
        }

        this.toggleAxesVisibility(seriesConfig)
    }

    public hideLoading(): void {
        this.isChartLoading = false
    }

    public clearOverlayText(): void {
        this.chartOverlayContainer.hide()
    }

    public onError(error: any): void {
        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

        ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
        LogHelper.warn(IMRChartController.LOG_PREFIX, `Unable to download data. Error: ${errorMsg}.`)
    }

    public showLoading(): void {
        this.isChartLoading = true
    }

    public showNoDataText(): void {
        this.chartOverlayContainer.showText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData))
    }

    public storeExtensionState(extensionState: any): void {
        const existingState: any = this.onStateRestoring() || {}

        existingState[this.currentExtension.extensionName] = extensionState

        this.onStateStoring(existingState)
    }

    public toggleSeries(seriesConfig: DevExpress.viz.ChartSeries): void {
        if (seriesConfig != null) {
            this.toggleSeriesVisibility(seriesConfig)
            this.toggleAxesVisibility(seriesConfig)
            this.toggleAxesColoring(seriesConfig)
            this.toggleNoSeriesText()
        }
    }

    // IIMRChartExtensionController Implementation End


    // IComponentController Implementation 

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize();
        }
    }

    // IComponentController Implementation End


    // Component

    private initialize(): void {
        this.isLoading = true

        this.setDefaults()
        this.setOptions()

        $.when(
            this.initializeTopToolbar(),
            this.initializeElementSizeWatch(),
            this.initializeFullscreenContainer(),
            this.initializeOverlayContainer(),
            this.initializeChart()
        )
            .then(() => {
                this.onInitialized()
            })
            .fail((error: any) => {
                LogHelper.warn(IMRChartController.LOG_PREFIX, "Unable to initialize IMRChart.", error)
            })
            .always(() => {
                this.isLoading = false
            })
    }

    // Component End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onStateStoring(state): void {
        if (this.options != null && _.isFunction(this.options.onStateStoring)) {
            this.options.onStateStoring(state)
        }
    }

    private onStateRestoring(): any {
        if (this.options != null && _.isFunction(this.options.onStateRestoring)) {
            return this.options.onStateRestoring()
        }

        return null
    }

    // Events End


    // Element Size Watch

    private initializeElementSizeWatch(): void {
        this.elementSizeWatchOptions = {
            onResize: () => this.refreshChart()
        }
    }

    // Element Size Watch End


    // Top Toolbar

    private initializeTopToolbar(): JQueryPromise<any> {
        this.topToolbarOptions = {
            items: []
        }

        this.topToolbarOptions.items.push({
            location: 'after',
            widget: 'dxButton',
            options: {
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Tooltip),
                icon: 'tips',

                onClick: (e: any) => this.toggleTooltipSharing()
            },
        })

        this.topToolbarOptions.items.push({
            location: 'after',
            widget: 'dxButton',
            options: {
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Legend),
                template: TemplateHelper.getIcon(ConfigHelper.Icon.LEGEND, this.userSession),

                onClick: (e: any) => this.toggleChartLegend()
            },
        })

        this.topToolbarOptions.items.push({
            location: 'after',
            widget: 'dxButton',
            options: {
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Rotate),
                template: TemplateHelper.getIcon(ConfigHelper.Icon.ROTATE, this.userSession),

                onClick: (e: any) => this.rotateChart()
            },
        })

        if (this.isFullscreenEnabled) {
            this.topToolbarOptions.items.push({
                location: 'after',
                widget: 'dxButton',
                options: {
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Fullscreen),
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.FULLSCREEN, this.userSession),

                    onClick: (e: any) => this.toggleFullscreen()
                },
            })
        }

        if (this.isToolbarEnabled) {
            return TypeHelper.onInitializedAll(this.topToolbarOptions)
        }

        return $.Deferred<any>().resolve().promise()
    }

    // Top Toolbar End


    // Common Chart

    private initializeChart(): JQueryPromise<any> {
        this.axisTitles = new Dictionary<string>(null)
        this.hiddenSeriesAxis = []

        return this.currentExtension
            .initialize(this)
            .then(() => {
                const toInitialize: (DevExpress.viz.charts.dxChartOptions | DevExpress.viz.charts.dxPieChartOptions)[] = []
                const chartOptions: DevExpress.viz.charts.dxChartOptions = this.currentExtension.getChartOptions()
                const pieChartOptions: DevExpress.viz.charts.dxPieChartOptions = this.currentExtension.getPieChartOptions()

                if (chartOptions != null) {
                    this.chartOptions = chartOptions

                    this.overwriteChartOptions()

                    toInitialize.push(this.chartOptions)
                }

                if (pieChartOptions != null) {
                    this.pieChartOptions = pieChartOptions

                    this.overwritePieChartOptions()

                    toInitialize.push(this.pieChartOptions)
                }

                return TypeHelper.onInitializedAll(...toInitialize)
            })
    }

    private overwriteChartOptions(): void {
        const state: any = this.getState()

        if (state.isLegendVisible != null) {
            if (this.chartOptions.legend == null) {
                this.chartOptions.legend = {}
            }

            this.chartOptions.legend.visible = state.isLegendVisible
        }

        if (state.isTooltipShared != null) {
            if (this.chartOptions.tooltip == null) {
                this.chartOptions.tooltip = {}
            }

            this.chartOptions.tooltip.shared = state.isTooltipShared
        }

        if (state.isRotated != null) {
            this.chartOptions.rotated = state.isRotated
        }

        this.chartOptions.onInitialized = _.wrap(this.chartOptions.onInitialized, (onInitializedOrg: Function, ...args: any[]) => {
            if (_.isFunction(onInitializedOrg)) {
                onInitializedOrg(...args)
            }

            this.onChartInitialized(args[0])
        }) as any

        this.chartOptions.onLegendClick = _.wrap(this.chartOptions.onLegendClick as Function, (onLegendClickOrg: Function, ...args: any[]) => {
            this.onChartLegendClick(args[0])

            if (_.isFunction(onLegendClickOrg)) {
                onLegendClickOrg(...args)
            }
        }) as any

        this.chartOptions.onSeriesClick = _.wrap(this.chartOptions.onSeriesClick as Function, (onSeriesClickOrg: Function, ...args: any[]) => {
            this.onChartSeriesClick(args[0])

            if (_.isFunction(onSeriesClickOrg)) {
                onSeriesClickOrg(...args)
            }
        }) as any
    }

    private overwritePieChartOptions(): void {
        const state: any = this.getState()

        if (state.isLegendVisible != null) {
            if (this.pieChartOptions.legend == null) {
                this.pieChartOptions.legend = {}
            }

            this.pieChartOptions.legend.visible = state.isLegendVisible
        }

        this.pieChartOptions.onInitialized = _.wrap(this.pieChartOptions.onInitialized, (onInitializedOrg: Function, ...args: any[]) => {
            if (_.isFunction(onInitializedOrg)) {
                onInitializedOrg(...args)
            }

            this.pieChart = args[0].component
        }) as any

        this.pieChartOptions.onLegendClick = _.wrap(this.pieChartOptions.onLegendClick as Function, (onLegendClickOrg: Function, ...args: any[]) => {
            this.onPieChartLegendClick(args[0])

            if (_.isFunction(onLegendClickOrg)) {
                onLegendClickOrg(...args)
            }
        }) as any
    }

    private refreshChart(): void {
        if (this.chart != null) {
            this.chart.render()
        }

        if (this.pieChart != null) {
            this.pieChart.render()
        }
    }

    private toggleChartLegend(): void {
        this.storeStateOption({
            isLegendVisible: !this.getState().isLegendVisible
        })

        if (this.chart != null) {
            this.chart.option('legend.visible', this.getState().isLegendVisible)
        }

        if (this.pieChart != null) {
            this.pieChart.option('legend.visible', this.getState().isLegendVisible)
        }
    }

    private toggleNoSeriesText(): void {
        if (this.chart != null) {
            const somechartSeriesVisible: boolean = _.some(this.chart.option('series'), (seriesConfig: DevExpress.viz.ChartSeries) => seriesConfig.visible)

            if (!somechartSeriesVisible) {
                this.chartOverlayContainer.showText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectSeries))
            } else {
                this.chartOverlayContainer.hide()
            }
        }

        // !!!TODO ogarnąć chowanie pie slice

        // if (this.pieChart != null) {
        //     const somePieChartSeriesVisible: boolean = _.some(this.pieChart.getAllSeries(), (series: DevExpress.viz.charts.PieSeries) => {
        //         return _.some(series.getAllPoints(), (point: DevExpress.viz.charts.PiePoint) => point.isVisible())
        //     })

        //     if (!hasVisibleSeries) {
        //         hasVisibleSeries = somePieChartSeriesVisible
        //     }
        // }
    }

    // Common Chart End


    // Chart

    private onChartInitialized(e: any): void {
        this.chart = e.component

        this.$timeout(() => {
            // To rescale the chart after init.
            this.chart.render()
        })
    }

    private onChartLegendClick(e: any): void {
        const currentSeries: DevExpress.viz.chartSeriesObject = e != null ? e.target : null

        if (currentSeries != null) {
            const seriesConfig: DevExpress.viz.ChartSeries = this.getSeriesConfigByName(currentSeries.name)

            this.toggleSeriesVisibility(seriesConfig)
            this.toggleAxesVisibility(seriesConfig)
            this.toggleAxesColoring(null)
            this.toggleNoSeriesText()
        }
    }

    private onChartSeriesClick(e: any): void {
        const currentSeries: DevExpress.viz.ChartSeries = e != null ? e.target : null

        if (currentSeries != null) {
            const seriesConfig: DevExpress.viz.ChartSeries = this.getSeriesConfigByName(currentSeries.name)

            this.toggleAxesColoring(seriesConfig)
        }
    }

    private rotateChart(): void {
        this.storeStateOption({
            isRotated: !this.getState().isRotated
        })

        if (this.chart != null) {
            this.chart.option('rotated', this.getState().isRotated)
        }
    }

    private toggleAxesColoring(seriesConfig: DevExpress.viz.ChartSeries): void {
        const valueAxes: DevExpress.viz.dxChartValueAxis[] = this.chart.option("valueAxis")

        if (_.isArray(valueAxes)) {
            valueAxes.forEach((axis: DevExpress.viz.dxChartValueAxis) => {
                if (seriesConfig != null && axis.name === seriesConfig.axis) {
                    const series = this.chart.getSeriesByName(seriesConfig.name)

                    if (series != null) {
                        axis.color = series.getColor()
                        axis.width = 3
                    }
                }
                else {
                    axis.color = '#d3d3d3'
                    axis.width = 1
                }
            })

            this.chart.option("valueAxis", this.chart.option("valueAxis"))
        }
    }

    private toggleAxesVisibility(seriesConfig: DevExpress.viz.ChartSeries): void {
        if (seriesConfig != null) {
            const seriesAxis: DevExpress.viz.dxChartValueAxis = this.getSeriesAxisConfigByName(seriesConfig.axis)

            if (seriesAxis != null) {
                const isSeriesHidden: boolean = !_.contains(this.hiddenSeriesAxis, seriesConfig.name)

                if (seriesConfig.visible && isSeriesHidden) {
                    seriesAxis.visible = true

                    _.assign(seriesAxis, { label: { visible: true } })
                    _.assign(seriesAxis, { grid: { visible: true } })

                    if (seriesAxis.title == null) {
                        seriesAxis.title = {}
                    }

                    seriesAxis.title.text = this.axisTitles.tryGetValue(seriesAxis.name)
                }
                else {
                    const seriesTitle: string = seriesAxis.title != null ? seriesAxis.title.text : null

                    if (seriesTitle != null) {
                        this.axisTitles.addOrUpdate(seriesAxis.name, seriesTitle)
                    }

                    const axisSeriesConfigs: DevExpress.viz.ChartSeries[] = this.getSeriesConfigByAxis(seriesAxis.name)
                    const allSeriesInvisible: boolean = axisSeriesConfigs.every((axisSeriesConfig: DevExpress.viz.ChartSeries) => !axisSeriesConfig.visible)
                    const allVisibleSeriesHidden: boolean = _.chain(axisSeriesConfigs)
                        .filter((axisSeriesConfig: DevExpress.viz.ChartSeries) => axisSeriesConfig.visible)
                        .every((axisSeriesConfig: DevExpress.viz.ChartSeries) => _.contains(this.hiddenSeriesAxis, axisSeriesConfig.name))
                        .value()

                    if (allSeriesInvisible || allVisibleSeriesHidden) {
                        seriesAxis.visible = false

                        _.assign(seriesAxis, { label: { visible: false } })
                        _.assign(seriesAxis, { grid: { visible: false } })

                        seriesAxis.title = null
                    }
                }
            }

            this.chart.option('valueAxis', this.chart.option('valueAxis'))
        }
    }

    private toggleSeriesVisibility(seriesConfig: DevExpress.viz.ChartSeries): void {
        if (seriesConfig != null) {
            seriesConfig.visible = !seriesConfig.visible

            this.chart.option('series', this.chart.option('series'))
        }
    }

    private toggleTooltipSharing(): void {
        this.storeStateOption({
            isTooltipShared: !this.getState().isTooltipShared
        })

        if (this.chart != null) {
            this.chart.option('tooltip.shared', this.getState().isTooltipShared)
        }
    }

    private getSeriesAxisConfigByName(axisName: string): DevExpress.viz.dxChartValueAxis {
        const valueAxes: DevExpress.viz.dxChartValueAxis[] = this.chart.option("valueAxis")

        return _.find(valueAxes, (valueAxis: DevExpress.viz.dxChartValueAxis) => valueAxis.name === axisName)
    }

    private getSeriesConfigByName(seriesName: string): DevExpress.viz.ChartSeries {
        const allSeriesConfig: DevExpress.viz.ChartSeries[] = this.chart.option('series')

        return _.find(allSeriesConfig, (seriesConfig: DevExpress.viz.ChartSeries) => seriesConfig.name === seriesName)
    }

    private getSeriesConfigByAxis(axisName: string): DevExpress.viz.ChartSeries[] {
        const allSeriesConfig: DevExpress.viz.ChartSeries[] = this.chart.option('series')

        return _.filter(allSeriesConfig, (seriesConfig: DevExpress.viz.ChartSeries) => seriesConfig.axis === axisName)
    }

    // Chart End


    // PieChart

    private onPieChartLegendClick(e: any): void {
        this.togglePieChartSliceVisibility(e)
        this.toggleNoSeriesText()
    }

    private togglePieChartSliceVisibility(e: any): void {
        const points: DevExpress.viz.piePointObject[] = e.points
        const point: DevExpress.viz.piePointObject = _.first(points)

        if (point != null) {
            point.isVisible() ? point.hide() : point.show()
        }
    }

    // PieChart End


    // Fullscreen Container

    private initializeFullscreenContainer(): JQueryPromise<any> {
        this.fullscreenContainerOptions = {
            fullscreenContainerSelector: '.layout-container',

            onInitialized: (e) => this.fullscreenContainer = e.component
        }

        return TypeHelper.onInitializedAll(this.fullscreenContainerOptions)
    }

    private toggleFullscreen(): void {
        if (this.fullscreenContainer != null) {
            this.fullscreenContainer.toggleFullscreen()
            this.isFullscreen = this.fullscreenContainer.isFullscreen()
        }
    }

    // Fullscreen Container End


    // Overlay Container 

    private initializeOverlayContainer(): JQueryPromise<any> {
        this.chartOverlayContainerOptions = {
            onInitialized: (component: IIMROverlayContainer) => this.chartOverlayContainer = component
        }

        return TypeHelper.onInitializedAll(this.chartOverlayContainerOptions)
    }

    // Overlay Container End


    // Options

    private setDefaults(): void {
        this.isFullscreenEnabled = true
        this.isToolbarEnabled = true

        this.currentExtension = new BaseChartExtension()
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.toolbar != null && this.options.toolbar.enabled != null) {
                this.isToolbarEnabled = this.options.toolbar.enabled
            }

            if (this.options.fullscreen != null && this.options.fullscreen.enabled != null) {
                this.isFullscreenEnabled = this.options.fullscreen.enabled
            }

            if (this.options != null && this.options.extension != null) {
                this.currentExtension = this.options.extension
            }
        }
    }

    // Options End


    // State

    private getState(): ChartState {
        return this.onStateRestoring() || {
            isLegendVisible: true,
            isRotated: false,
            isTooltipShared: false
        }
    }

    private storeStateOption(state: any): void {
        const existingState: any = this.getState()

        this.onStateStoring(_.assign(existingState, state))
    }

    // State End
}
